// Default design size used by animators when exporting boosted jackpot resources.
const DEFAULT_DESIGN_WIDTH = 540;
const DEFAULT_DESIGN_HEIGHT = 960;

export type SpineBoneResizeConfig = {
  boneName: string,
  scaleMode: 'max' | 'min',
  center?: boolean,
};

export type HandleSpineResizeOptions = {
  designWidth?: number,
  designHeight?: number,
};

export type HandleSpineResizeResult = {
  apply: () => void,
  dispose: () => void,
};

/**
 * Globally resizes specific spine bones based on window size.
 *
 * - `scaleMode: 'max'` -> bone.scale = Math.max(window.w/designW, window.h/designH) (cover behavior, e.g. background)
 * - `scaleMode: 'min'` -> bone.scale = Math.min(window.w/designW, window.h/designH) (contain behavior, e.g. noScale)
 * - `center: true` -> bone is recentered to (0, 0) in skeleton space.
 *
 * Automatically attaches a `resize` listener; call the returned `dispose()` to detach.
 */
export const handleSpineResize = (
  spine: any,
  bones: SpineBoneResizeConfig[],
  options: HandleSpineResizeOptions = {}
): HandleSpineResizeResult => {
  const designWidth = options.designWidth ?? DEFAULT_DESIGN_WIDTH;
  const designHeight = options.designHeight ?? DEFAULT_DESIGN_HEIGHT;

  const apply = () => {
    if (!spine?.skeleton) return;

    const ratioX = window.innerWidth / designWidth;
    const ratioY = window.innerHeight / designHeight;
    const maxScale = Math.max(ratioX, ratioY);
    const minScale = Math.min(ratioX, ratioY);

    bones.forEach(({ boneName, scaleMode, center }) => {
      const bone = spine.skeleton.findBone?.(boneName);
      if (!bone) return;

      const scale = scaleMode === 'max' ? maxScale : minScale;
      bone.scaleX = scale;
      bone.scaleY = scale;

      if (center) {
        bone.x = 0;
        bone.y = 0;
      }
    });

    if (typeof spine.skeleton.updateWorldTransform === 'function') {
      spine.skeleton.updateWorldTransform();
    }
  };

  apply();
  window.addEventListener('resize', apply);

  const dispose = () => {
    window.removeEventListener('resize', apply);
  };

  return { apply, dispose };
};
